/**
 * Helpers para mapear DisparoMessagePayload → campos UAZAPI /sender/*.
 */

import type { DisparoMessagePayload, DisparoMessageType } from "@/lib/disparos-types";
import type { SenderMessageType } from "@/lib/disparos-types";
import {
  buildFabInteractiveFromButtonForm,
  buildFabInteractiveFromListForm,
  buildFabInteractiveFromPollForm,
  slugifyInteractiveId,
  type FabInteractiveCanonical,
} from "@/lib/whatsapp-interactive-message";

const INTERACTIVE_TYPES = new Set<DisparoMessageType>(["list", "button", "poll", "carousel"]);

export function isDisparoInteractiveType(type: DisparoMessageType): boolean {
  return INTERACTIVE_TYPES.has(type);
}

export function buildFabInteractiveFromDisparoMessage(
  message: DisparoMessagePayload,
): FabInteractiveCanonical | null {
  const extra = message.extra ?? {};
  if (extra.fab_interactive && typeof extra.fab_interactive === "object") {
    return extra.fab_interactive as FabInteractiveCanonical;
  }

  const text = String(message.text ?? "").trim();
  const choices = Array.isArray(extra.choices)
    ? extra.choices.map((c) => String(c ?? "").trim()).filter(Boolean)
    : [];

  if (message.type === "button") {
    const buttons = choices.length
      ? choices.map((c, i) => {
          const [title, id] = c.split("|");
          const label = (title ?? c).trim();
          return { title: label, id: (id ?? label).trim() || slugifyInteractiveId(label, i, "btn") };
        })
      : Array.isArray(extra.buttons)
        ? (extra.buttons as { title: string }[])
        : [];
    if (!text || buttons.length === 0) return null;
    return buildFabInteractiveFromButtonForm(text, buttons);
  }

  if (message.type === "list") {
    const listButton = String(extra.listButton ?? "Ver opções").trim();
    let rows: { title: string; description?: string; section?: string }[] = [];

    if (Array.isArray(extra.listRows)) {
      rows = extra.listRows as { title: string; description?: string; section?: string }[];
    } else if (choices.length) {
      let currentSection: string | undefined;
      for (const line of choices) {
        const sectionMatch = line.match(/^\[(.+)\]$/);
        if (sectionMatch) {
          currentSection = sectionMatch[1].trim();
          continue;
        }
        const parts = line.split("|");
        rows.push({
          title: (parts[0] ?? line).trim(),
          description: parts[2]?.trim(),
          section: currentSection,
        });
      }
    }

    if (!text || rows.length === 0) return null;
    return buildFabInteractiveFromListForm(text, listButton, rows);
  }

  if (message.type === "poll") {
    const options = choices.length
      ? choices
      : Array.isArray(extra.options)
        ? (extra.options as string[])
        : [];
    const question = String(extra.question ?? text).trim();
    if (!question || options.length < 2) return null;
    return buildFabInteractiveFromPollForm(question, options);
  }

  if (message.type === "carousel" && Array.isArray(extra.carousel)) {
    return {
      kind: "carousel",
      bodyText: text,
      carousel: { cards: extra.carousel as FabInteractiveCanonical["carousel"] extends { cards: infer C } ? C : never },
    };
  }

  return null;
}

export function mapDisparoMessageToSenderFields(message: DisparoMessagePayload): {
  type: SenderMessageType;
  text?: string;
  file?: string;
  docName?: string;
  choices?: string[];
  footerText?: string;
  listButton?: string;
  selectableCount?: number;
  imageButton?: string;
  carousel?: unknown;
  fab_interactive?: FabInteractiveCanonical;
} {
  if (!isDisparoInteractiveType(message.type)) {
    if (message.type === "contact" || message.type === "location") {
      throw new Error(`Tipo "${message.type}" ainda não suportado em disparos`);
    }
    return {
      type: message.type as SenderMessageType,
      text: message.text ?? message.caption,
      file: message.file,
      docName: message.docName,
    };
  }

  const fabInteractive = buildFabInteractiveFromDisparoMessage(message);
  if (!fabInteractive) {
    throw new Error(`Payload interativo inválido para tipo "${message.type}"`);
  }

  const extra = message.extra ?? {};

  if (message.type === "button") {
    return {
      type: "button",
      text: fabInteractive.bodyText,
      choices: fabInteractive.buttons?.map((b) => `${b.title}|${b.id}`),
      footerText: extra.footerText ? String(extra.footerText) : fabInteractive.footerText,
      fab_interactive: fabInteractive,
    };
  }

  if (message.type === "list") {
    const choices: string[] = [];
    let section: string | null = null;
    for (const row of fabInteractive.listRows ?? []) {
      if (row.section && row.section !== section) {
        choices.push(`[${row.section}]`);
        section = row.section;
      }
      choices.push(
        row.description
          ? `${row.title}|${row.id}|${row.description}`
          : `${row.title}|${row.id}`,
      );
    }
    return {
      type: "list",
      text: fabInteractive.bodyText,
      choices,
      listButton: fabInteractive.menuButton,
      footerText: extra.footerText ? String(extra.footerText) : fabInteractive.footerText,
      fab_interactive: fabInteractive,
    };
  }

  if (message.type === "poll") {
    return {
      type: "poll",
      text: fabInteractive.poll?.question ?? fabInteractive.bodyText,
      choices: fabInteractive.poll?.options,
      selectableCount: fabInteractive.poll?.allowMultiple
        ? fabInteractive.poll.options.length
        : 1,
      fab_interactive: fabInteractive,
    };
  }

  return {
    type: "carousel",
    text: fabInteractive.bodyText,
    carousel: fabInteractive.carousel?.cards,
    fab_interactive: fabInteractive,
  };
}
